import axios, { AxiosResponse } from 'axios';
import JSZip from 'jszip';
import {JSONRPCClient, TypedJSONRPCClient} from "json-rpc-2.0";
import {AIAPIMethods, APIMethods, IShareProject, IShareScenario} from "common";
import { AssetPermissions } from 'common/gql';
import { CmsScenario } from 'common/Session';
import Scenario from './scenarioElements/Scenario';
import Input from './Input';
import AssetsManager from './AssetsManager';
import WorkspaceManager from './WorkspaceManager';
import GQLAssets from './api/GQL/assets/GQLassets';
import GQLProjects from './api/GQL/projects/GQLprojects';
import QuizManager from './QuizManager';
import Metadata from './MetaData';
import { JsonCopy } from './utilities/Misc';
import { ISaveServerResponse, Save } from "../interfaces/Responses";
import { ProjectStates } from '../interfaces/ProjectStates';
import { FileIOType, SimulationStates, ExportType, CustomEventTypes} from '../globals';
import store, { StoreActions } from '../state/store';
import config from '../config';
import { GrapesRenderModes, IGrapesJSProjectData } from '../interfaces/VisageUI';
import { RenderGrapesToHTML } from '../components/visageUI/GrapesJSUtilities';
import { ScenarioMetadata } from '../interfaces/API';

document.addEventListener(CustomEventTypes.saveWlcSettings, () => APIUtility.saveWlcSettings());

document.addEventListener(CustomEventTypes.saveWlcSettings, () => APIUtility.saveWlcSettings());

document.addEventListener(CustomEventTypes.saveWlcSettings, () => APIUtility.saveWlcSettings());

export enum SaveTypes {
	MANUAL =  'manual',
	AUTOSAVE = 'auto'
}

// JSONRPCClient needs to know how to send a JSON-RPC request.
// Tell it by passing a function to its constructor. The function must take a JSON-RPC request and send it.
const jsonRpcClient: TypedJSONRPCClient<APIMethods & AIAPIMethods> = new JSONRPCClient((jsonRPCRequest) =>
	fetch(`${config.SERVER_ADDRESS}/json-rpc`, {
		method: "POST",
		headers: {
			"content-type": "application/json",
			"Authorization": axios.defaults.headers.common['Authorization'] as string
		},
		body: JSON.stringify(jsonRPCRequest),
	}).then((response) =>
	{
		if (response.status === 200)
		{
			// Use client.receive when you received a JSON-RPC response.
			return response
				.json()
				.then((jsonRPCResponse) => jsonRpcClient.receive(jsonRPCResponse));
		}
		else if (jsonRPCRequest.id !== undefined)
		{
			return Promise.reject(new Error(response.statusText));
		}
	})
);

async function zipStepUIData()
{
// render all UI data into separate HTML files, and zip them together
	const uiDataEntries = Object.entries<IGrapesJSProjectData>(store.getState().visageModule.stepUIData);
	const zip = new JSZip();

	for await(const [key, value] of uiDataEntries)
	{
		const step = Scenario.getStep(key);
		if (!step || step.isDeleted()) continue;

		if(!value || !Object.keys(value).length) continue;

		const renderedHTML = await RenderGrapesToHTML(value, GrapesRenderModes.Publish);
		zip.file(key + ".html", renderedHTML);
	}

	// add the zip file to the request
	return await zip.generateAsync({type:"blob"});
}

const apiCallsInProgress = new Map<string, boolean>();

export default class APIUtility
{
	static createProject = async (name: string, metadata?: ScenarioMetadata) =>
	{
		return await GQLProjects.createProject(name, metadata);
	}

	static getProject = async(projectId: string) =>
	{
		const res = await axios.get(config.SERVER_ADDRESS + config.GET_PROJECT_PATH, { params: { project: projectId } });
		return APIUtility.handleResult(res);
	}

	static openProject = async(projectId: string) =>
	{
		const res = await axios.get(config.SERVER_ADDRESS + config.GET_PROJECT_PATH, { params: { project: projectId, opened: true } });
		return APIUtility.handleResult(res);
	}

	static deleteProject = async(projectId: string) =>
	{
		const res = await axios.delete(config.SERVER_ADDRESS + config.DELETE_PROJECT_PATH, { params: { project: projectId } });
		return APIUtility.handleResult(res);
	}

	static saveProject = async (blob: Blob, saveType: string = SaveTypes.MANUAL, assets, projectId, duplicate = false) =>
	{
		const currentScenario = Scenario.current();
		if(!currentScenario)
		{
			console.error("No current scenario found for saving");
			return;
		}

		const formData = new FormData();
		formData.set('project', projectId);
		formData.set('createVersion', config.CREATE_VERSION);
		formData.set('extensionsVersion', config.SCOPE_EXTENSION_VERSION);
		formData.set('assets', JSON.stringify(assets));
		formData.set('name', currentScenario.name);
		formData.set('duplicate', String(duplicate));
		formData.set('type', saveType);
		formData.set('size', blob.size.toString());
		formData.append('file', blob);
		const axiosConfig = {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		};

		const res = await axios.post(config.SERVER_ADDRESS + config.SAVE_PROJECT_PATH, formData, axiosConfig);
		if (res)
		{
			return res;
		}
		else
		{
			throw new Error('Unknown error occurred');
		}
	}

	static setProjectState = async (state: ProjectStates, projectId: string) =>
	{
		const formData = new FormData();
		formData.set('project', projectId);
		formData.set('state', state);
		const res = await axios.post(config.SERVER_ADDRESS + config.UPDATE_PROJECT_STATUS_PATH, formData);
		APIUtility.handleResult(res);
	}

	static publishOffline = async (blob: Blob, assets: string[] = [], publishType, additionalBlobMap = {}) =>
	{
		const currentScenario = Scenario.current();
		if(!currentScenario)
		{
			console.error("No current scenario found for publishing");
			return;
		}
	
		const formData = new FormData();
		formData.set('project', currentScenario.id);
		formData.set('createVersion', config.CREATE_VERSION);
		formData.set('extensionsVersion', config.SCOPE_EXTENSION_VERSION);
		formData.set('name', currentScenario.name);
		formData.set('assets', JSON.stringify(assets));
		formData.set('publishType', publishType);
		formData.set('addedMetadata', JSON.stringify(Metadata.calculateProjectMetadata(blob.size)));
		formData.append('file', blob);
	
		const zipFile = await zipStepUIData();
		formData.append('uiData', zipFile);
	
		const axiosConfig = {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		};
	
		for (const key of Object.keys(additionalBlobMap))
		{
			formData.append(key, additionalBlobMap[key]);
		}
		const res = await axios.post(config.SERVER_ADDRESS + config.PUBLISH_OFFLINE_PROJECT_PATH, formData, axiosConfig);
		console.log(res);
		if (res.status === 200)
		{
			const publish_info = {
				sdk_version : config.CREATE_VERSION,
				published_at: new Date().toISOString(),
				session_type : publishType,
				user_info : store.getState().userModule.sessionInfo as any, //TODO user_info is of type IPublishUserInfo, but sessionInfo is of type IUserSession. they only share the email field, is that the only thing required?
				size: blob.size,
			};
			store.dispatch(StoreActions.scenario.setScenarioPublishInfo( publish_info ));
				
			return {
				status: 'success',
				url: res.data.exportUrl
			};
		}
		else if (res.data && res.data.error)
		{
			throw new Error(res.data.error);
		}
		else
		{
			throw new Error('Unknown error occurred');
		}
	}
	
	static publishProject = async (blob: Blob, encrypted = false, recipientGroups: string[] = [], publishType, assets: string[] = [], additionalBlobMap = {}, resourcePermissions: IShareScenario["replaceResourcePermissions"]) =>
	{
		const currentScenario = Scenario.current();
		if(!currentScenario)
		{
			console.error("No current scenario found for publishing");
			return;
		}
	
		const extras = {}
		if(QuizManager.getQuizType())
		{
			extras["quizSettings"] = store.getState().checklistModule.quizSettings;
			extras["checklistData"] = QuizManager.packQuizReduxData();
		}
		//console.log(extras);
	
		const formData = new FormData();
		formData.set('project', currentScenario.id);
		formData.set('createVersion', config.CREATE_VERSION);
		formData.set('extensionsVersion', config.SCOPE_EXTENSION_VERSION);
		formData.set('groups', JSON.stringify(recipientGroups));
		formData.set('publishType', publishType);
		formData.set('name', currentScenario.name);
		formData.set('assets', JSON.stringify(assets));
		formData.set('encrypted', String(encrypted));
		formData.set('externalData', JSON.stringify({extras: extras}));
		formData.set('addedMetadata', JSON.stringify(Metadata.calculateProjectMetadata(blob.size)));
		formData.set('resourcePermissions', JSON.stringify(resourcePermissions));
		formData.append('file', blob);
	
		const zipFile = await zipStepUIData();
		formData.append('uiData', zipFile);
	
		const axiosConfig = {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		};
	
		for (const key of Object.keys(additionalBlobMap))
		{
			formData.append(key, additionalBlobMap[key]);
		}
		const res = await axios.post(config.SERVER_ADDRESS + config.PUBLISH_PROJECT_PATH, formData, axiosConfig);
		console.log(res);
		if (res.status === 200)
		{
			const publish_info = {
				sdk_version : config.CREATE_VERSION,
				published_at: new Date().toISOString(),
				session_type : publishType,
				user_info : store.getState().userModule.sessionInfo as any, //TODO user_info is of type IPublishUserInfo, but sessionInfo is of type IUserSession. they only share the email field, is that the only thing required?
				size: blob.size,
			};
			store.dispatch(StoreActions.scenario.setScenarioPublishInfo( publish_info ));
	
			return {
				status: 'success',
				bundle: res.data.bundleId
			};
		}
		else if (res.data && res.data.error)
		{
			throw new Error(res.data.error);
		}
		else
		{
			throw new Error('Unknown error occurred');
		}
	}

	static loadSaves = async (projectId: string, top = 1): Promise<ISaveServerResponse> =>
	{
		const res = await GQLProjects.loadProjectWithSaveFiles(projectId, top);
		return res;
	}

	static approveEula = async (version = config.EULA_VERSION) =>
	{
		const urlencoded = new URLSearchParams();
		urlencoded.append("version", version);
		const res = await axios.post(config.SERVER_ADDRESS + config.EULA_APPROVE_PATH, urlencoded);
		return APIUtility.handleResult(res);
	}

	static setReleaseNotesSeen = async (version = config.CREATE_VERSION) =>
	{
		const urlencoded = new URLSearchParams();
		urlencoded.append("version", version);
		const res = await axios.post(config.SERVER_ADDRESS + config.RELEASE_NOTES_SEEN_PATH, urlencoded);
		return APIUtility.handleResult(res);
	}

	static lastSavedWorkspace = {};
	static saveTimeout: NodeJS.Timeout | null = null;
	static lastSettings: string | null = null;

	static saveWlcSettings = async () =>
	{
		// Clear any pending save
		if (APIUtility.saveTimeout) 
		{
			clearTimeout(APIUtility.saveTimeout);
		}

		// Debounce the save operation
		APIUtility.saveTimeout = setTimeout(async () => 
		{
			const state = store.getState();
			const isViewOnlyMode = state.applicationModule.isViewOnlyMode;
			if(isViewOnlyMode) return; // view-only mode shouldn't save settings anyway

			//Make sure the saved layout is the last one that was not of a special state
			const editingSlide = state.uiModule.editingSlide;
			const currentStep = Scenario.getStep(state.scenarioModule.currentStepId!);
			const simulationState = state.applicationModule.simulationState;
			const shouldSaveLastLayout = !(simulationState === SimulationStates.Stopped &&
				currentStep && !currentStep.isDefault() && !currentStep.isTrackersStep() && 
				!editingSlide);
			APIUtility.lastSavedWorkspace = WorkspaceManager.save(shouldSaveLastLayout);
					
			const userSettings = {
				preferences: JsonCopy(store.getState().preferencesModule.editorPreferences),
				// keybindings: Input.KeyBindings, // included in `preferences` above
				uiPreview: {
					visible: state.uiModule.previewVisible,
					scale: state.uiModule.previewScale,
					aspect: state.uiModule.previewAspect,
				},
				visageUI: {
					displayDevice: state.visageModule.currentDisplayDevice,
					editorSettings: state.visageModule.editorSettings,
				},
				view: {
					spritesVisible: state.workspaceModule.spritesVisible,
					trackersVisible: state.workspaceModule.trackersVisible,
					gridVisible: state.workspaceModule.gridVisible,
					uiVisible: state.uiModule.previewVisible,
				},
				publishSettings: state.applicationModule.publishSettings,
				worklink: state.worklinkModule.settings,
				workspace: JsonCopy(APIUtility.lastSavedWorkspace),
			};

			const serializedSettings = JSON.stringify(userSettings);
			
			// Only save if the settings have actually changed
			if (serializedSettings === APIUtility.lastSettings) 
			{
				return;
			}			
			APIUtility.lastSettings = serializedSettings;

			const urlencoded = new URLSearchParams();
			urlencoded.append("settings", serializedSettings);			

			const res = await axios.post(config.SERVER_ADDRESS + config.UPDATE_WLC_SETTINGS_PATH, urlencoded);
			return APIUtility.handleResult(res, 'Error occurred while saving settings:' + res.status);
			
		}, 500); // 500ms debounce
	}

	static getModelJobs = async () =>
	{
		if(apiCallsInProgress.get(this.getModelJobs.name) === true)
		{
			return;
		}
		apiCallsInProgress.set(this.getModelJobs.name, true);

		const res = await axios.get(config.SERVER_ADDRESS + config.LIST_JOBS_PATH)
			.finally(() =>
			{
				apiCallsInProgress.set(this.getModelJobs.name, false);
			});
		return APIUtility.handleResult(res, 'Error occured while requesting model-jobs!');
	}

	static createNewModelJob = async (assetId, revisionName, operations, baseRevision = null, rootFileName = null) =>
	{
		const urlencoded = new URLSearchParams();
		urlencoded.append("asset", assetId);
		urlencoded.append("name", revisionName);
		if (baseRevision)
		{
			urlencoded.append("base", baseRevision);
		}
		else if (rootFileName)
		{
			urlencoded.append("root", rootFileName);
		}
		const opsJson = JSON.stringify(operations);
		urlencoded.append("operations", opsJson);

		const res = await axios.post(config.SERVER_ADDRESS + config.NEW_JOB_PATH, urlencoded);
		return APIUtility.handleResult(res, 'Error occured while creating a new model-job!');
	}

	static getAssetArchiveInfo = async (assetId) =>
	{
		const res = await axios.get(config.SERVER_ADDRESS + config.ASSET_ARCHIVE_INFO + '?asset=' + assetId);
		return APIUtility.handleResult(res, 'Error occured while trying to get archive information');
	}

	static getRevisionMetadata = async (revisionId) =>
	{
		const requestUrl =
			config.SERVER_ADDRESS +
			config.DOWNLOAD_FILE_PATH +
			'?asset=' +
			revisionId +
			'&state=metadata' +
			'&jwt=' + store.getState().userModule.sessionInfo!.accessToken;

		const res = await axios.get(requestUrl, {
			transformRequest: [(data, headers) =>
			{
				delete headers.Authorization;
				return data;
			}]
		});
		return APIUtility.handleResult(res, "Error occured with status " + res.status + " when performing a request for the metadata file of revision: " + revisionId);
	}

	static getJobForRevision = async (revisionId) =>
	{
		const res = await axios.get(config.SERVER_ADDRESS + config.GET_JOB_FOR_REVISION_PATH + "?revision=" + revisionId);
		return APIUtility.handleResult(res, "Error occured with status " + res.status + " when performing a request for the job of revision: " + revisionId);
	}

	static importProject = async (blob, projectId?: string) =>
	{
		projectId = projectId || Scenario.current()?.id;
		if (!projectId) 
		{
			console.error("No project ID provided for importing project");
			return;
		}

		const formData = new FormData();
		formData.set('project', projectId);
		formData.append('file', blob);

		let cancel;
		// eslint-disable-next-line import/no-named-as-default-member
		const token = new axios.CancelToken(function executor(c) {cancel = c;});

		const axiosConfig = {
			headers: {
				'Content-Type': 'multipart/form-data'
			},
			onUploadProgress: (p) =>
			{
				AssetsManager.registerFileLoadProgress({ 'id': 'import', 'name': projectId, 'progress': (p.loaded / p.total), "type": FileIOType.Import, "cancel": cancel });
			},
			cancelToken: token,
		};

		const res = await axios.post(config.SERVER_ADDRESS + config.IMPORT_PROJECT_PATH, formData, axiosConfig);
		return APIUtility.handleResult(res);
	}

	static mergeProject = async (blob, projectId?: string) =>
	{
		projectId = projectId || Scenario.current()?.id;
		if (!projectId) 
		{
			console.error("No project ID provided for merging project");
			return;
		}

		const formData = new FormData();
		formData.set('project', projectId);
		formData.append('file', blob);
	
		let cancel;
		// eslint-disable-next-line import/no-named-as-default-member
		const token = new axios.CancelToken(function executor(c) {cancel = c;});
	
		const axiosConfig = {
			headers: {
				'Content-Type': 'multipart/form-data'
			},
			onUploadProgress: (p) =>
			{
				AssetsManager.registerFileLoadProgress({ 'id': 'import', 'name': projectId, 'progress': (p.loaded / p.total), "type": FileIOType.Import, "cancel": cancel });
			},
			cancelToken: token,
		};
	
		const res = await axios.post(config.SERVER_ADDRESS + config.MERGE_PROJECT_PATH, formData, axiosConfig);
		return APIUtility.handleResult(res);
	}

	static exportProject = async (packageType: ExportType, projectId?: string) =>
	{
		projectId = projectId || Scenario.current()?.id;
		if (!projectId) 
		{
			console.error("No project ID provided for exporting project");
			return;
		}

		const urlencoded = new URLSearchParams();
		urlencoded.append('project', projectId);
		urlencoded.append('packagetype', packageType);

		const res = await axios.post(config.SERVER_ADDRESS + config.EXPORT_PROJECT_PATH, urlencoded);
		return APIUtility.handleResult(res);
	}

	static exportProjectDownload = async (packageType: ExportType, save: Save) =>
	{
		const urlencoded = new URLSearchParams();
		urlencoded.append('project', save.project);
		urlencoded.append('packagetype', packageType);
		urlencoded.append('revisionId', save.revisionId);

		const res = await axios.post(config.SERVER_ADDRESS + config.EXPORT_PROJECT_REVISION_PATH, urlencoded, {responseType: 'blob'});
		return APIUtility.handleResult(res);
	}

	static getImportExportList = async (projectId?: string) =>
	{
		if(apiCallsInProgress.get(this.getImportExportList.name) === true) 
		{
			return;
		}

		projectId = projectId || Scenario.current()?.id;
		if (!projectId) 
		{
			console.warn("No project ID provided for importing/exporting project");
			return;
		}

		apiCallsInProgress.set(this.getImportExportList.name, true);
		const res = await axios.get(config.SERVER_ADDRESS + config.IMPORT_EXPORT_PROJECT_LIST_PATH + '?project=' + projectId)
			.finally(() =>
			{
				apiCallsInProgress.set(this.getImportExportList.name, false);
			});
		return APIUtility.handleResult(res);
	}

	static getMergeList = async (projectId?: string) =>
	{
		projectId = projectId || Scenario.current()?.id;
		if (!projectId) 
		{
			console.warn("No project ID provided for merging project");
			return;
		}
	
		const res = await axios.get(config.SERVER_ADDRESS + config.IMPORT_MERGE_PROJECT_LIST_PATH + '?project=' + projectId);
		return APIUtility.handleResult(res);
	}

	static listDemoProjectFiles = async () =>
	{
		const res = await axios.get(config.SERVER_ADDRESS + config.LIST_DEMO_PROJECT_PATH);
		return APIUtility.handleResult(res);
	}

	static importDemoProject = async (demoKey: string, projectId?: string) =>
	{
		projectId = projectId || Scenario.current()?.id;
		if(!projectId)
		{
			console.warn("No project ID provided for importing demo project");
			return;
		}

		const formData = new FormData();
		formData.set('project', projectId);
		formData.set('demoKey', demoKey);

		const res = await axios.post(config.SERVER_ADDRESS + config.IMPORT_DEMO_PROJECT_PATH, formData);
		return APIUtility.handleResult(res);
	}

	static handleResult = (res: AxiosResponse<any, any>, errorMessage: string = 'Unknown error occurred') =>
	{
		if (res.status === 200 && (res.data || res.data === "")) return res.data;
		if (res.data && res.data.error) throw new Error(res.data.error);

		throw new Error(errorMessage);
	}
	
	static async generatePreviews(gltf: object | ArrayBuffer): Promise<string[]>
	{
		let buffer: Blob;
		let format: "gltf" | "glb";
		let fileName: string;

		if ( gltf instanceof ArrayBuffer )
		{
			// const decoder = new TextDecoder('utf-8');
			// buffer = decoder.decode(gltf);
			format = "glb"
			buffer = new Blob([gltf], { type: 'application/octet-stream' });
			fileName = 'model.glb'; // Set a file name with the correct extension
		}
		else
		{
			buffer = new Blob([JSON.stringify(gltf)], { type: 'application/json' });
			format = "gltf";
			fileName = 'model.gltf'; // Set a file name with the correct
		}

		const formData = new FormData();
		formData.append('file', buffer, fileName);
		formData.set('format', format);

		let cancel;
		// eslint-disable-next-line import/no-named-as-default-member
		const token = new axios.CancelToken(function executor(c) {cancel = c;});

		const axiosConfig = {
			headers: {
				'Content-Type': 'multipart/form-data'
			},
			onUploadProgress: (p) =>
			{
				AssetsManager.registerFileLoadProgress({ 'id': 'import', 'name': "GLB", 'progress': (p.loaded / p.total), "type": FileIOType.Import, "cancel": cancel });
			},
			cancelToken: token,
		};

		const res = await axios.post(config.SERVER_ADDRESS + config.NEW_PREVIEW_RENDER_JOB_PATH, formData, axiosConfig);
		return APIUtility.handleResult(res);
	}


	static setProjectResourcePermissions = async (
		projectId = Scenario.current()?.id,
		resourcePermissions: IShareProject["replaceResourcePermissions"]
	) =>
	{
		if(projectId === 'debug')
		{
			return true;
		}

		const res = await axios.post(config.SERVER_ADDRESS + config.SET_PROJECT_RESOURCE_PERMISSIONS_PATH, {
			project: projectId,
			resource_permissions: resourcePermissions
		});
		return APIUtility.handleResult(res);
	}

	static getMe = async (token?: string) =>
	{
		axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

		return axios.get(config.SERVER_ADDRESS + config.ME_PATH, {
			withCredentials: true,
			headers: {
				"Authorization": `Bearer ${token}`
			}
		});
	}

	static getAssetInfo = async (assetId: string) =>
	{
		if(!assetId) 
		{
			console.error("No asset ID provided for getting asset info");
			return;
		}

		return await axios.get(config.SERVER_ADDRESS + config.GET_ASSET_PATH + '?asset=' + assetId);
	}

	/**
	 * Since we're moving to direct S3 uploads we need to first get a signed URL from the server for the file name
	 *
	 * @static
	 * @param {string} assetName
	 * @memberof APIUtility
	 */
	static getUrlForAssetUpload = async (assetName: string) => 
	{
		if(!assetName) return;
		
		const formData = new FormData();
		formData.set('requestFileUploadUrl', 'true');
		formData.set('fileName', assetName);
		return await axios.post(`${config.SERVER_ADDRESS}${config.UPLOAD_FILE_PATH}`, formData);
	}
	
	/**
	 * After uploading an asset to S3 we need to verify that it was uploaded successfully
	 *
	 * @static
	 * @param {string} assetId
	 * @memberof APIUtility
	 */
	static verifyAssetUploaded = async (assetId: string) =>
	{
		if(!assetId) return;

		const formData = new FormData();
		formData.set('asset', assetId);
		return await axios.post(`${config.SERVER_ADDRESS}${config.VERIFY_FILE_PATH}`, formData);
	}

	static getAccessToken() 
	{
		const sessionInfo = store.getState().userModule.sessionInfo;
		if(!sessionInfo) return;

		return sessionInfo.accessToken;
	}

	static getAssetsList = async () => 
	{
		try 
		{
			const token  = APIUtility.getAccessToken();
			if(!token) return;
			
		    const assets = await GQLAssets.getAssetsList();
       
		    // map GQL assets to a format that the client expects
		    // user assets are assumed to be managed
		    // we remove the check and hardcode the permission
		    // for performance reasons
		    const userAssetsMap = GQLAssets.generateAssetMap(
		        assets.user,
		        {
		            "__typename": "AssetPermissions",
		            "canManage": {
		                "__typename": "AuthorizationResult",
		                "value": true
		            }
		        } as AssetPermissions);

		    const orgAssetsMap = GQLAssets.generateAssetMap(assets.organization);

		    return {
		        'user': userAssetsMap,
		        'organization': orgAssetsMap
		    };
		}
		catch (e) 
		{
		    console.error(e);
		}
	}
}

declare const window: Window & { api: any };

if ( window.api === undefined )
{
	window.api = APIUtility;
}